<template>
    <div class="ds-ex-history">
        <div v-if="status === 'loading'" class="ds-ex-history-state"><Loader /></div>
        <div v-else-if="status === 'error'" class="ds-ex-history-state" role="alert">
            <span>Не удалось загрузить историю</span>
            <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="emit('retry')">Повторить</button>
        </div>
        <div v-else-if="!records.length" class="ds-ex-history-state ds-ex-muted">Событий пока нет</div>
        <template v-else>
            <template v-for="group in groups" :key="group.day">
                <div class="ds-ex-history-day">{{ group.title }}</div>
                <div v-for="record in group.records" :key="record.id" class="ds-ex-event">
                    <span class="ds-ex-event-icon" :class="`is-${meta(record).tone}`" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <path v-if="meta(record).icon === 'plus'" d="M12 5v14M5 12h14" />
                            <path v-else-if="meta(record).icon === 'pencil'" d="M4 20h4L19 9l-4-4L4 16v4z" />
                            <path v-else-if="meta(record).icon === 'check'" d="M5 12l5 5L20 7" />
                            <path v-else-if="meta(record).icon === 'cross'" d="M6 6l12 12M18 6 6 18" />
                            <template v-else>
                                <circle cx="12" cy="8" r="3.5" />
                                <path d="M5 20c1.2-3.5 3.8-5 7-5s5.8 1.5 7 5" />
                            </template>
                        </svg>
                    </span>
                    <div class="ds-ex-event-body">
                        <div class="ds-ex-strong">{{ title(record) }}</div>
                        <div v-if="record.changes?.length" class="ds-ex-event-changes">
                            <template v-for="change in record.changes" :key="change.field">
                                <span class="ds-ex-muted">{{ FIELD_LABELS[change.field] ?? change.field }}</span>
                                <span><s class="ds-ex-muted">{{ change.old_display ?? '—' }}</s> → <b>{{ change.new_display ?? '—' }}</b></span>
                            </template>
                        </div>
                        <div v-else-if="record.message" class="ds-ex-muted">{{ record.message }}</div>
                    </div>
                    <span class="ds-ex-muted ds-ex-event-time">{{ toLocal(record.created_at).format('HH:mm') }}</span>
                </div>
            </template>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Loader from '@/ui/Loader.vue'
import { dayMonthLabel, shortName, fullName, todayKey, toLocal } from './exchangeFormat'
import type { HistoryRecord } from './exchangeTypes'

const props = defineProps<{ records: HistoryRecord[], status: 'loading' | 'ready' | 'error' }>()
const emit = defineEmits<{ retry: [] }>()

// Подписи полей структурированных изменений (target-contract §3); message для значений не разбирается.
const FIELD_LABELS: Record<string, string> = {
    date: 'Дата',
    dttm_work_start_at: 'Начало',
    dttm_work_end_at: 'Окончание',
    position: 'Должность',
    department: 'Магазин',
    exchange_type: 'Биржа',
    inner_exchange: 'Внутренняя биржа',
    outer_exchange: 'Внешняя биржа',
    comment: 'Комментарий',
    salary_hour: 'Ставка',
    requested_department: 'Из магазина',
    requested_user: 'Сотрудник',
    is_request_rejected: 'Запрос отклонён',
    deletion_reason: 'Причина',
    assigned_shift_id: 'Смена',
}

type Tone = 'success' | 'muted' | 'violet' | 'teal' | 'amber' | 'danger'
const ACTIONS: Record<string, { label: string, icon: string, tone: Tone, subject: boolean }> = {
    created: { label: 'Создана', icon: 'plus', tone: 'success', subject: false },
    updated: { label: 'Изменена', icon: 'pencil', tone: 'muted', subject: false },
    response: { label: 'Отклик', icon: 'person', tone: 'violet', subject: true },
    confirmed: { label: 'Назначен', icon: 'check', tone: 'success', subject: true },
    rejected: { label: 'Отклонён', icon: 'cross', tone: 'danger', subject: true },
    response_withdrawn: { label: 'Отклик отозван', icon: 'cross', tone: 'muted', subject: true },
    deleted: { label: 'Снята', icon: 'cross', tone: 'danger', subject: false },
    shift_cancelled: { label: 'Смена отменена', icon: 'cross', tone: 'muted', subject: false },
    checkin: { label: 'Чек-ин', icon: 'check', tone: 'teal', subject: true },
    checkout: { label: 'Чек-аут', icon: 'check', tone: 'teal', subject: true },
}

const meta = (record: HistoryRecord) => ACTIONS[record.action] ?? { label: record.action, icon: 'person', tone: 'muted' as Tone, subject: false }

function title(record: HistoryRecord) {
    const info = meta(record)
    const person = info.subject ? (record.subject_user ?? record.user) : record.user
    const name = info.subject ? fullName(person) : shortName(person)
    return name ? `${info.label} · ${name}` : info.label
}

// Сервер отдаёт created_at desc; группы по календарному дню подразделения в том же порядке.
const groups = computed(() => {
    const today = todayKey()
    const result: Array<{ day: string, title: string, records: HistoryRecord[] }> = []
    for (const record of props.records) {
        const day = toLocal(record.created_at).format('YYYY-MM-DD')
        let group = result.find(item => item.day === day)
        if (!group) {
            const label = dayMonthLabel(day)
            group = { day, title: (day === today ? `Сегодня, ${label}` : label).toUpperCase(), records: [] }
            result.push(group)
        }
        group.records.push(record)
    }
    return result
})
</script>
